import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Divider from 'shared/components/Divider/Divider';
import mapEntitiesToOptions from 'shared/lib/mapEntitiesToOptions';
import { Input, Submit, Select, Checkbox, Radio } from 'shared/components/Form';
import { userportal as routes } from 'shared/routes';
import { selectors as fieldSelectors } from 'userportal/ducks/fields';
import { selectors as langSelectors } from 'userportal/ducks/languages';
import styles from './Form.module.scss';

const Form = ({
  email,
  values,
  hasError,
  errors,
  onSubmit,
  onChange,
  onPasswordChange,
  processing
}) => {
  const { t } = useTranslation('userportal');
  const fieldsList = mapEntitiesToOptions(useSelector(fieldSelectors.getAllFields), 'id', 'slug')
    .map(field => ({ value: field.value, label: t(`common:field.${field.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const languages = mapEntitiesToOptions(useSelector(langSelectors.geti18nLanguages), 'code', 'slug');
  const allLanguages = mapEntitiesToOptions(useSelector(langSelectors.getAllLanguages), 'id', 'slug');

  const languagesList = languages
    .map(language => ({ value: language.value, label: t(`common:language.${language.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const allLanguagesList = allLanguages
    .map(language => ({ value: language.value, label: t(`common:language.${language.label}`) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <form className={styles.invitation} onSubmit={onSubmit}>
      <div className="row">
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Input
            iconClass="la la-key"
            error={errors.password}
            name="password"
            value={values.password}
            type="password"
            onChange={onPasswordChange}
            label={t('common:form.password')}
          />
        </div>
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Input
            iconClass="la la-key"
            error={errors.passwordCheck}
            name="passwordCheck"
            value={values.passwordCheck}
            type="password"
            onChange={onPasswordChange}
            label={t('common:form.passwordCheck')}
          />
        </div>
      </div>
      <Divider spacing="large">{t('page.invitation.signupData')}</Divider>
      <div className="row">
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Input
            iconClass="la la-user"
            error={errors.firstname}
            name="firstname"
            value={values.firstname}
            type="text"
            onChange={onChange}
            label={t('common:form.firstname')}
          />
        </div>
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Input
            iconClass="la la-user"
            error={errors.lastname}
            name="lastname"
            value={values.lastname}
            type="text"
            onChange={onChange}
            label={t('common:form.lastname')}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Input
            disabled
            iconClass="la la-at"
            value={email}
            type="text"
            label={t('common:form.emailLogin')}
          />
        </div>
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Select
            fullWidth
            label={t('common:userProfile.rolesSkills.primaryField')}
            name="primaryFieldId"
            options={fieldsList}
            value={values.primaryFieldId}
            onChange={onChange}
          />
        </div>
      </div>
      <div className="row mb8">
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Select
            fullWidth
            label={t('common:form.nativeLanguage')}
            name="nativeLanguage"
            options={allLanguagesList}
            value={values.nativeLanguage}
            onChange={onChange}
          />
        </div>
        <div className="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-xs-12">
          <Select
            fullWidth
            label={t('common:form.language')}
            name="preferredLanguage"
            options={languagesList}
            value={values.preferredLanguage}
            onChange={onChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <h4>{t('common:userProfile.preferences.lookingForJobLabel')}</h4>
        </div>
      </div>
      <div className="row mb16">
        <div className="col-12">
          <Radio
            name="lookingForJob"
            value={2}
            label={t('common:lookingForJob.2')}
            onChange={onChange}
            checked={values.lookingForJob === 2}
          />
          <Radio
            name="lookingForJob"
            value={1}
            label={t('common:lookingForJob.1')}
            onChange={onChange}
            checked={values.lookingForJob === 1}
          />
          <Radio
            name="lookingForJob"
            value={0}
            label={t('common:lookingForJob.0')}
            onChange={onChange}
            checked={values.lookingForJob === 0}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Checkbox
            name="agreement"
            error={errors.agreement}
            value={values.agreement}
            onChange={onChange}
            label={t('page.signup.agreement')}
            fullWidth
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12 mb16">
          <p className={styles.agreement}>
            {t('page.signup.agreementLinks')}
            {' '}
            <a href={routes.tos} target="_blank" rel="noopener noreferrer">{t('footer.tos')}</a>
            {', '}
            <a href={routes.gdpr} target="_blank" rel="noopener noreferrer">{t('footer.gdpr')}</a>.
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-12 centerXs">
          <Submit
            title={t('page.invitation.submit')}
            disabled={hasError}
            onClick={onSubmit}
            processing={processing}
          />
        </div>
      </div>
    </form>
  );
};

Form.propTypes = {
  email: PropTypes.string,
  values: PropTypes.object,
  hasError: PropTypes.bool,
  errors: PropTypes.object,
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  onPasswordChange: PropTypes.func,
  processing: PropTypes.bool
};

Form.defaultProps = {
  email: '',
  values: {},
  hasError: false,
  errors: {},
  onSubmit() {},
  onChange() {},
  onPasswordChange() {},
  processing: false
};

export default Form;
